import { createContext, useState, useContext, ReactNode, Dispatch, SetStateAction } from 'react';
import {
  CalenderDatesUi,
  EmployeesResourceScheduleDataUi,
} from '../types/GetEmployeesResourceScheduleResponseUi';
import { ScheduleWeekDaySwedish } from '../types/ScheduleWeekDaySwedish';
import { TimeReportStatus } from '../types/backendTypes/TimeReportStatus';
import dayjs, { Dayjs } from 'dayjs';
import { DeliveredArticle } from '../pages/workingSchedule/types/DeliveredArticle';

// Define the context value type
type UserContextType = {
  details: EmployeesResourceScheduleDataUi;
  month: string;
  date: CalenderDatesUi;
  setAssignmentInProgress: Dispatch<SetStateAction<{ id: string }>>;
  assignmentInProgress: { id: string };
  setStartedStopWatchTime: Dispatch<SetStateAction<Dayjs>>;
  startedStopWatchTime: Dayjs;
  stopWatchIsRunning: { isRunning: boolean; assignmentId: string };
  setStopWatchIsRunning: Dispatch<SetStateAction<{ isRunning: boolean; assignmentId: string }>>;
  setDetails: Dispatch<SetStateAction<EmployeesResourceScheduleDataUi>>;
  setDetailsDate: Dispatch<SetStateAction<CalenderDatesUi>>;
  setDetailsMonth: Dispatch<SetStateAction<string>>;
  setSelectedDateCard: Dispatch<SetStateAction<number>>;
  selectedDateCard: number;
  deliveredArticles: DeliveredArticle[];
  reportedMissingArticles: DeliveredArticle[];
  setReportedMissingArticles: Dispatch<SetStateAction<DeliveredArticle[]>>;
  setDeliveredArticles: Dispatch<SetStateAction<DeliveredArticle[]>>;
  chosenDeliveryType: string;
  setChosenDeliveryType: Dispatch<SetStateAction<string>>;
};

// Create the context
export const WorkersScheduleContext = createContext<UserContextType | undefined>(undefined);

// Custom hook to use the UserContext
export const useWorkerSchedule = (): UserContextType => {
  const context = useContext(WorkersScheduleContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// Provider component
export const WorkerScheduleProvider = ({ children }: { children: ReactNode }) => {
  const [chosenDeliveryType, setChosenDeliveryType] = useState('');
  // set default to 14 since that is always the middle of the calender where todays date always is
  const [selectedDateCard, setSelectedDateCard] = useState(7);
  const [deliveredArticles, setDeliveredArticles] = useState<DeliveredArticle[]>([]);
  const [reportedMissingArticles, setReportedMissingArticles] = useState<DeliveredArticle[]>([]);
  const [details, setDetails] = useState<EmployeesResourceScheduleDataUi>({
    customer: {
      address: '',
      name: '',
      id: '',
    },
    date: '',
    status: TimeReportStatus.NotReported,
    endTime: '',
    assignmentId: '',
    projectId: '',
    lastCheckedInEmployee: {
      date: '',
      name: '',
    },
    workMinutes: null,
    startTime: '',
    tasks: [],
    order: {
      address: {
        id: '',
        street: '',
        zipCode: '',
        city: '',
        latitude: 0,
        longitude: 0,
        projects: [],
        organizations: [],
        employees: [],
      },
      codes: '',
      id: 0,
      reportedMissingProducts: [],
      keys: '',
      notes: '',
      serviceName: '',
      contacts: [],
    },
    interval: {} as any,
    sequenceNumber: 0,
    transportationStartTime: '',
    transportationEndTime: '',
  });
  const [assignmentInProgress, setAssignmentInProgress] = useState<{
    id: string;
  }>({ id: '' });
  const [month, setDetailsMonth] = useState<string>('');
  const [startedStopWatchTime, setStartedStopWatchTime] = useState<Dayjs>(dayjs());
  const [stopWatchIsRunning, setStopWatchIsRunning] = useState<{
    isRunning: boolean;
    assignmentId: string;
  }>({ assignmentId: '', isRunning: false });
  const [date, setDetailsDate] = useState<CalenderDatesUi>({
    date: '',
    datesDay: '',
    hasScheduleData: false,
    weekDay: ScheduleWeekDaySwedish.Friday,
  });

  return (
    <WorkersScheduleContext.Provider
      value={{
        chosenDeliveryType,
        setChosenDeliveryType,
        details,
        month,
        date,
        setDetailsMonth,
        setDetailsDate,
        setDetails,
        assignmentInProgress,
        setAssignmentInProgress,
        setStartedStopWatchTime,
        setStopWatchIsRunning,
        stopWatchIsRunning,
        startedStopWatchTime,
        selectedDateCard,
        setSelectedDateCard,
        deliveredArticles,
        reportedMissingArticles,
        setDeliveredArticles,
        setReportedMissingArticles,
      }}
    >
      <>{children}</>
    </WorkersScheduleContext.Provider>
  );
};
